<template>
  <b-container fluid>
    <page-title icon='fas fa-tachometer-alt' :countdown='selectedLocation ? countdown : null' :countdown-max='60'>
      <translate>Delivery Date Monitor</translate>
    </page-title>
    <b-row>
      <b-col>
        <b-card>
          <b-card-text>
            <b-row>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <!-- d-block: the select is width:90% (room for the spinner) and labels are
                       inline-block, so without this the label jumps beside the select whenever
                       ~10% of the column fits it (e.g. no vertical scrollbar) and wraps when it
                       doesn't — keep it above, like the other filters. -->
                  <label for="destination" class="d-block" v-translate>Destination</label>
                  <template v-if="useDestinationAutocomplete">
                    <geofence-autocomplete id="destination" @selected="setDestination" :placeholder="$gettext('Search Geofence...')" v-if="!selectedLocation" />
                    <div v-else>
                      <b-button size="sm" variant="danger" @click.stop="clearDestination" style="padding: 2px 3px; line-height: 1">
                        <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 13px; vertical-align: middle" />
                      </b-button>
                      {{ selectedDestinationText }}
                    </div>
                  </template>
                  <b-form-select v-else id='destination' v-model='selectedLocation' :options='locations' size='sm'
                    style="width: 90%;" @change='getMonitor'>
                    <template #first>
                      <option :value='null' disabled v-translate>Select a location</option>
                    </template>
                  </b-form-select>
                  <b-spinner small style="vertical-align: middle;" v-if="working" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="program" v-translate>Program</label>
                  <b-form-input id="program" type="text" v-model="programField" @change="getMonitor" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="commodity-variety-remote" v-translate>Variety</label>
                  <commodity-variety-remote-autocomplete id="commodity-variety-remote" ref="commodityVarietyAutocomplete" @selected="setCommodityVariety" v-if="!commodityVariety.id" />
                  <div v-else>
                    <b-button size="sm" variant="danger" @click.stop="commodityVariety = {}; getMonitor();" style="padding: 2px 3px; line-height: 1">
                      <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 13px; vertical-align: middle" />
                    </b-button>
                    {{commodityVariety.external_id | cleanentityprefix}} ({{commodityVariety.reference}})
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="delivery-date" v-translate>Delivery Date</label>
                  <b-form-input id="delivery-date" type="date" v-model="date" @change="getMonitor" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="selectedLocation && shippings.length">
      <b-col>
        <b-card>
          <b-row>
            <b-col cols="12" md="3">
              <doughnut
                v-if="selectedLocation && shippings.length"
                :chart-options="chartOptions"
                :chart-data="chartData"
                id="doughnut-chart"
                dataset-id-key="label"
                :plugins="chartHtmlLegendPlugin ? [chartHtmlLegendPlugin] : []"
                :width="300"
                :height="100"
                style="max-height: 200px;"
              />
            </b-col>
            <b-col class="align-self-center">
              <div id="legend-container"></div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="selectedLocation && shippings.length">
      <b-col id="shipping-cards">
        <div v-for="shipping in shippings.filter(x => !hiddenStatuses.includes(x.status))" :key="shipping.id">
          <b-card v-if="shipping.distance || shipping.duration" class="mb-1 p-0">
          <b-row>
            <b-col>
              #{{ shipping.id | uuid }}
              <br />
              <b-button size="sm" @click.prevent v-b-modal="'modalShippingMap' + shipping.id" :disabled="!shipping.address_from || !shipping.address_to">
                <font-awesome-icon icon="map-marked-alt" size="2x" style="font-size: .9rem" />
              </b-button>
              <modal-shipping-map :shipping="shipping" update-every="30" without-button with-tracking />
            </b-col>
            <b-col>
              <dl class="mb-0">
                <dt v-translate>Order</dt>
                <dd class="mb-0">{{shipping.external_order_id | cleanentityprefix}}</dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0" v-if="shipping.meta.geofences.from">
                <dt v-translate>Origin</dt>
                <dd class="mb-0">
                  <strong>
                    {{shipping.meta.geofences.from.name}}
                  </strong>
                  <div><small>({{shipping.meta.geofences.from.city}})</small></div>
                </dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0">
                <dt v-translate>Program</dt>
                <dd class="mb-0">{{shipping.program_field}}</dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0">
                <dt v-translate>Status</dt>
                <dd class="mb-0"><span :style="{color: statuses.find(s => s.label === shipping.status)?.color || '#000000'}">{{statusStrings[shipping.status] || shipping.status}}</span></dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0" v-if="shipping.items?.[0]">
                <dt>{{ecosystemModules.items.label || this.$gettext('Items')}}</dt>
                <dd class="mb-0" v-b-tooltip :title="$gettextInterpolate($gettext('Last tracking sent on %{date}'), {date: moment.utc(shipping.items?.[0].item_tracking?.last_position_updated_at).tz($tz).format('L LT')})"><span class="badge mr-1 badge-dark h6">{{shipping.items[0].reference_code | cleanentityprefix}}</span></dd>
              </dl>
            </b-col>
            <b-col v-if="shipping.distance">
              <dl class="mb-0">
                <dt v-translate>Distance</dt>
                <dd class="mb-0">{{distance(shipping.distance)}}</dd>
              </dl>
            </b-col>
            <b-col v-if="shipping.duration">
              <dl class="mb-0">
                <dt v-translate :title="$gettext('Estimated Time of Completion')">E.T.C.</dt>
                <dd class="mb-0">{{moment.duration(shipping.duration, 'seconds').format('d [days] h [hrs] m [min]')}}</dd>
              </dl>
            </b-col>
            <b-col v-if="shipping.distance && shipping.duration">
              <dl class="mb-0">
                <dt v-translate :title="$gettext('Estimated Time of Arrival')">E.T.A.</dt>
                <dd v-show="shipping.eta" class="mb-0">
                  <span :title="moment.utc().add(shipping.eta, 'seconds').tz($tz).format('L LT')" v-b-tooltip>{{moment.duration(shipping.eta, 'seconds').format('d [days] h [hrs] m [min]')}}</span>
                  <eta-source :shipping="shipping" />
                </dd>
                <dd v-show="!shipping.eta" class="mb-0" v-translate>N/A</dd>
              </dl>
            </b-col>
            <b-col>
              <user-card v-if="shipping.courier" :user="shipping.courier" :profile-picture="false" card-style="micro" :with-map="false" without-id with-company></user-card>
            </b-col>
          </b-row>
        </b-card>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale
} from 'chart.js'
import constants from '@/common/constants'
import ShippingsMixin from '@/mixins/shippings.mixin'
import CommodityVarietyRemoteAutocomplete from '@/components/Autocompletes/CommodityVarietyRemoteAutocomplete'
import GeofenceAutocomplete from '@/components/Autocompletes/GeofenceAutocomplete'
import ModalShippingMap from '@/components/Maps/ModalShippingMap'
import EtaSource from '@/components/Shippings/EtaSource'

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale)

export default {
  name: 'DeliveryDateMonitor',
  title() {
    return this.$gettext('Delivery Date Monitor')
  },
  components: {
    CommodityVarietyRemoteAutocomplete,
    GeofenceAutocomplete,
    ModalShippingMap,
    Doughnut,
    EtaSource
  },
  data() {
    return {
      intervalSummary: null,
      intervalCountdown: null,
      countdown: 60,
      working: false,
      selectedLocation: null,
      selectedDestinationText: '',
      programField: '',
      commodityVariety: {},
      date: this.moment().format('YYYY-MM-DD'),
      defaultLocations: [
        {
          text: 'GALLO - LIVINGSTON WINERY (LIVINGSTON) - RC3.GMS-30',
          value: 'RC3.GMS-30'
        },
        {
          text: 'GALLO - TURNER ROAD VINTNERS (LODI) - RC3.GMS-775',
          value: 'RC3.GMS-775'
        },
        {
          text: 'GALLO - FRESNO WINERY (FRESNO) - RC3.GMS-20',
          value: 'RC3.GMS-20'
        },
        {
          text: 'GALLO - J VINEYARDS & WINERY (HEALDSBURG) - RC3.GMS-336',
          value: 'RC3.GMS-336'
        }
      ],
      statuses: [
        {
          label: 'INTRANSIT',
          color: '#01CCE0'
        },
        {
          label: 'ATPICKUPPOINT',
          color: '#3172F6'
        },
        {
          label: 'CONFIRMED',
          color: '#343a40'
        },
        {
          label: 'INCIDENCE',
          color: '#ffc107'
        },
        {
          label: 'READY',
          color: '#060'
        },
        {
          label: 'DRAFT',
          color: '#ccc'
        },
        {
          label: 'DELIVERED',
          color: '#28a745'
        }
      ],
      shippings: [],
      chartData: {
        labels: [],
        datasets: [
          {
            backgroundColor: [],
            data: []
          }
        ]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        rotation: 0,
        borderJoinStyle: 'round',
        borderColor: '#ffffff',
        borderWidth: 2,
        borderRadius: 10,
        spacing: 0,
        weight: 100,
        cutout: '90%',
        plugins: {
          htmlLegend: {
            containerID: 'legend-container'
          },
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            position: 'nearest',
            callbacks: {
              title: (context) => {
                return this.statusStrings[context[0].label] || context[0].label
              },
              label: (context) => {
                const value = context.parsed || 0
                const sum = context.dataset.data.reduce((a, b) => a + b, 0)
                const percentage = sum ? (value / sum * 100).toFixed(2) : 0
                return `${value} (${percentage}%)`
              }
            }
          }
        }
      },
      chartHtmlLegendPlugin: this.htmlLegendPlugin(),
      hiddenStatuses: []
    }
  },
  mixins: [constants, ShippingsMixin],
  computed: {
    userDestinationsEnabled() {
      return !!this.ecosystemModules.deliveryDateMonitor?.userDestinations
    },
    metaDestinations() {
      const destinations = this.profile?.meta?.deliveryDateMonitor?.destinations
      return Array.isArray(destinations) && destinations.length > 0 ? destinations : null
    },
    // Admins without an assignment search among all geofences — there is no "full
    // list" to offer (tens of thousands). Restricted users never get the autocomplete:
    // they see their assigned destinations or fall back to the fixed default list.
    useDestinationAutocomplete() {
      return this.userDestinationsEnabled && !this.metaDestinations && (this.isSuperAdmin || this.isGcmAdmin)
    },
    locations() {
      if (this.userDestinationsEnabled && this.metaDestinations) {
        return this.metaDestinations
      }
      return this.defaultLocations
    }
  },
  mounted() {
    this.setTimeout()
  },
  beforeDestroy() {
    clearTimeout(this.intervalSummary)
    clearInterval(this.intervalCountdown)
  },
  methods: {
    setTimeout() {
      clearTimeout(this.intervalSummary)
      this.intervalSummary = setTimeout(() => {
        this.setTimeout()
      }, 60000)
      this.getMonitor()
    },
    setIntervalCountdown() {
      clearInterval(this.intervalCountdown)
      this.intervalCountdown = setInterval(function () {
        this.countdown = this.countdown === 0 ? 0 : this.countdown - 1
      }.bind(this), 1000)
    },
    getOrCreateLegendList(chart, id) {
      const legendContainer = document.getElementById(id)
      let listContainer = legendContainer.querySelector('ul')
      let totalContainer = document.createElement('div')

      if (!listContainer) {
        listContainer = document.createElement('ul')
        listContainer.className = 'd-flex d-md-block'
        listContainer.style.margin = '10px 0'
        listContainer.style.padding = 0

        legendContainer.appendChild(listContainer)

        totalContainer.id = 'total-container'
        totalContainer.className = 'text-center text-md-left'
        totalContainer.style.margin = 0
        totalContainer.style.padding = '0 40px'

        legendContainer.appendChild(totalContainer)
      }
      totalContainer = document.getElementById('total-container')
      totalContainer.innerHTML = this.$gettextInterpolate(this.$gettext('Total: <strong>%{total}</strong>'), { total: this.shippings.length })

      return listContainer
    },
    htmlLegendPlugin() {
      const that = this
      return {
        id: 'htmlLegend',
        afterUpdate(chart, args, options) {
          const ul = that.getOrCreateLegendList(chart, options.containerID)

          // Remove old legend items
          while (ul.firstChild) {
            ul.firstChild.remove()
          }

          // Reuse the built-in legendItems generator
          const items = chart.options.plugins.legend.labels.generateLabels(chart)

          items.forEach(item => {
            const li = document.createElement('li')
            li.style.alignItems = 'center'
            li.style.cursor = 'pointer'
            li.style.display = 'flex'
            li.style.flexDirection = 'row'
            li.style.marginLeft = '10px'
            li.style.marginBottom = '10px'

            li.onclick = () => {
              const { type } = chart.config
              if (type === 'pie' || type === 'doughnut') {
                // Pie and doughnut charts only have a single dataset and visibility is per item
                chart.toggleDataVisibility(item.index)
                that.toggleDataVisibility(item.index)
              } else {
                chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))
              }
              chart.update()
            }

            // Color box
            const boxSpan = document.createElement('span')
            boxSpan.style.background = item.fillStyle
            boxSpan.style.borderColor = item.strokeStyle
            boxSpan.style.borderWidth = item.lineWidth + 'px'
            boxSpan.style.display = 'inline-block'
            boxSpan.style.flexShrink = 0
            boxSpan.style.height = '12px'
            boxSpan.style.marginRight = '10px'
            boxSpan.style.width = '20px'

            // Text
            const textContainer = document.createElement('p')
            textContainer.style.color = item.fontColor
            textContainer.style.margin = 0
            textContainer.style.padding = 0
            textContainer.style.textDecoration = item.hidden ? 'line-through' : ''

            const text = document.createTextNode(item.text + ' (' + that.chartData.datasets[0].data[item.index] + ')')
            textContainer.appendChild(text)

            li.appendChild(boxSpan)
            li.appendChild(textContainer)
            ul.appendChild(li)
          })
        }
      }
    },
    setCommodityVariety(commodityVariety) {
      this.commodityVariety = commodityVariety
      this.getMonitor()
    },
    setDestination(geofence) {
      // Raw external_id (prefix included): destination_location matches against it.
      this.selectedLocation = geofence.external_id
      this.selectedDestinationText = `${geofence.name}${geofence.reference_location ? ` (${geofence.reference_location})` : ''} - ${geofence.external_id}`
      this.getMonitor()
    },
    clearDestination() {
      this.selectedLocation = null
      this.selectedDestinationText = ''
    },
    toggleDataVisibility(index) {
      if (this.hiddenStatuses.includes(this.statuses[index].label)) {
        this.hiddenStatuses = this.hiddenStatuses.filter(s => s !== this.statuses[index].label)
      } else {
        this.hiddenStatuses.push(this.statuses[index].label)
      }
    },
    getMonitor() {
      if (this.selectedLocation) {
        this.working = true
        this.countdown = 60
        this.setIntervalCountdown()

        const params = {
          page: 0,
          filter_groups: [{
            filters: [
              ['delivery_date', 'bt', [
                this.moment(this.date).tz(this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
                this.moment(this.date).tz(this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
              ]],
              ['destination_location', 'eq', this.selectedLocation],
              ['status', 'in', ['draft', 'pending', 'canceled'], true]
            ]
          }],
          includes: ['shippings.courier.courier_available.tracking', 'shippings.items.item_tracking', 'shippings.status_history']
        }
        if (this.programField) {
          params.filter_groups[0].filters.push(['program_field', 'eq', this.programField])
        }
        if (this.commodityVariety.id) {
          params.filter_groups[0].filters.push(['commodity_variety_id', 'eq', this.commodityVariety.id])
        }

        this.$http.get(this.$appConfig.api.baseUrl + 'shipping-orders', { params })
          .then(response => {
            const stuff = {}
            this.shippings = []
            response.body.data.forEach(order => {
              const programField = order.program_field ?? ''
              this.shippings = [
                ...this.shippings,
                ...order.shippings
                  .filter(shipping => this.statuses.map(s => s.label).includes(shipping.status))
                  .map(shipping => ({ ...shipping, program_field: programField }))
              ]
            })
            this.statuses.forEach(status => {
              stuff[status.label] = this.shippings.filter(shipping => shipping.status === status.label)
            })

            this.chartData.labels = this.statuses.map(status => this.statusStrings[status.label] || status.label)
            this.chartData.datasets[0].backgroundColor = this.statuses.map(status => status.color)
            this.chartData.datasets[0].data = this.statuses.map(status => stuff[status.label].length)

            // Spreading into fresh objects means this whole-array assignment is what
            // Vue observes, so the ETA keys are reactive without needing $set.
            this.shippings = this.shippings.map(shipping => ({ ...shipping, ...this.getShippingEta(shipping) }))
            const statusOrder = this.statuses.map(s => s.label)
            this.shippings = this.shippings.sort((a, b) => {
              // 1. Group by status following the order of this.statuses
              const statusA = statusOrder.indexOf(a.status)
              const statusB = statusOrder.indexOf(b.status)
              if (statusA !== statusB) return statusA - statusB

              // 2. Within same status group: sort by eta asc if present, otherwise by updated_at desc
              const aHasEta = a.eta !== null && a.eta !== undefined
              const bHasEta = b.eta !== null && b.eta !== undefined

              if (aHasEta && bHasEta) return a.eta - b.eta
              if (aHasEta && !bHasEta) return -1
              if (!aHasEta && bHasEta) return 1

              // Both without eta: sort by updated_at descending
              return new Date(b.updated_at) - new Date(a.updated_at)
            })
            this.working = false
          })
      }
    }
  }
}
</script>

<style lang='scss'>
.canvasjs-chart-credit {
  display: none !important
}
#shipping-cards {
  .card-body {
    padding: 10px 15px;
  }
}
</style>
